import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamBillingDTO } from "@shaarp/common";
import { useState } from "react";
import { ButtonLoading } from "../button-loading";

export const CardBillingEmail = ({ billing }: { billing: TeamBillingDTO }) => {
  const toastError = useToastError();
  const { toast } = useToast();
  const api = useApi();
  const { t } = useTranslation();

  const [email, setEmail] = useState<string>(billing.email || "");
  const [invoiceEmailEnabled, setInvoiceEmailEnabled] = useState<boolean>(
    billing.invoiceEmailEnabled
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isToggling, setIsToggling] = useState<boolean>(false);

  // Toggling the switch persists immediately (with a confirmation toast and
  // rollback on error), so the user gets instant feedback without needing to
  // click "Save".
  const handleToggleReception = async (newValue: boolean) => {
    const trimmedEmail = email.trim();

    // Reception can only be enabled when an email is set.
    if (newValue && !trimmedEmail) {
      toastError({
        title: t("team.components.billingEmail.toast.emailRequired.title"),
        description: t(
          "team.components.billingEmail.toast.emailRequired.description"
        ),
      });
      return;
    }

    const previousValue = invoiceEmailEnabled;
    setInvoiceEmailEnabled(newValue);
    try {
      setIsToggling(true);
      await api.post(`/my/team/billing`, {
        billing: {
          invoiceEmailEnabled: newValue,
          ...(newValue ? { email: trimmedEmail } : {}),
        },
      });
      toast({
        variant: "default",
        title: t("team.components.billingEmail.receiveInvoices.toast.title"),
        description: newValue
          ? t("team.components.billingEmail.receiveInvoices.toast.enabled")
          : t("team.components.billingEmail.receiveInvoices.toast.disabled"),
      });
    } catch {
      setInvoiceEmailEnabled(previousValue);
      toastError();
    } finally {
      setIsToggling(false);
    }
  };

  const save = async () => {
    const trimmedEmail = email.trim();

    if (invoiceEmailEnabled && !trimmedEmail) {
      toastError({
        title: t("team.components.billingEmail.toast.emailRequired.title"),
        description: t(
          "team.components.billingEmail.toast.emailRequired.description"
        ),
      });
      return;
    }

    try {
      setIsSaving(true);
      await api.post(`/my/team/billing`, {
        billing: {
          email: trimmedEmail || null,
          invoiceEmailEnabled,
        },
      });
      toast({
        variant: "default",
        title: t("team.components.billingEmail.toast.success.title"),
        description: t(
          "team.components.billingEmail.toast.success.description"
        ),
      });
    } catch {
      toastError();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="invoice-email-enabled">
              {t("team.components.billingEmail.receiveInvoices.label")}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t("team.components.billingEmail.receiveInvoices.description")}
            </p>
          </div>
          <Switch
            id="invoice-email-enabled"
            checked={invoiceEmailEnabled}
            onCheckedChange={handleToggleReception}
            disabled={isToggling}
          />
        </div>
        <form>
          <Input
            placeholder={t("team.components.billingEmail.placeholder")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </form>
      </CardContent>
      <CardFooter className="px-6 py-4 border-t">
        <ButtonLoading onClick={save} isLoading={isSaving}>
          {t("team.components.billingEmail.saveButton")}
        </ButtonLoading>
      </CardFooter>
    </Card>
  );
};
